import { useState } from "react";
import { toast } from "sonner";
import { Phone, PhoneCall, Smartphone, Printer, Mail, MapPin, Globe, Send, Loader2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHeader } from "../components/shared/PageHeader";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { submitInquiry } from "../lib/api";
import { CONTACT, COMPANY, IMAGES } from "../data/content";

const DETAILS = [
  { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}` },
  { icon: PhoneCall, label: "Toll Free", value: CONTACT.tollFree, href: `tel:${CONTACT.tollFree.replace(/\s/g, "")}` },
  { icon: Smartphone, label: "Mobile", value: CONTACT.mobile, href: `tel:${CONTACT.mobile.replace(/\s/g, "")}` },
  { icon: Printer, label: "E-Fax", value: CONTACT.eFax },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: MapPin, label: "Address", value: CONTACT.address },
  { icon: Globe, label: "Website", value: COMPANY.website },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [auditguard, setAuditguard] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry({ type: "contact", ...form, auditguard_early_access: auditguard });
      toast.success("Message sent. Our team will be in touch shortly.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
      setAuditguard(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        testId="contact-header"
        eyebrow="Contact"
        title="Let's talk about your aviation objectives."
        subtitle="Reach out for a consultation or a capability discussion. We respond to every inquiry personally."
        image={IMAGES.jetTarmac}
      />

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">Contact details</h2>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {COMPANY.name}, a division of {COMPANY.parent}.
                </p>
                <ul className="mt-8 space-y-5">
                  {DETAILS.map((d) => (
                    <li key={d.label} className="flex items-start gap-4">
                      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                        <d.icon className="h-5 w-5" />
                      </span>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{d.label}</div>
                        {d.href ? (
                          <a href={d.href} data-testid={`contact-detail-${d.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm font-medium text-navy hover:text-brand">{d.value}</a>
                        ) : (
                          <div className="text-sm font-medium text-navy">{d.value}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <form onSubmit={onSubmit} data-testid="contact-form" className="rounded-3xl border border-border bg-white p-8 shadow-sm lg:p-10">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="name">Full name *</Label>
                      <Input id="name" data-testid="contact-name" value={form.name} onChange={update("name")} className="mt-2" placeholder="Jane Doe" />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={update("email")} className="mt-2" placeholder="jane@airline.com" />
                    </div>
                    <div>
                      <Label htmlFor="company">Company / Organization</Label>
                      <Input id="company" data-testid="contact-company" value={form.company} onChange={update("company")} className="mt-2" placeholder="Your organization" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} className="mt-2" placeholder="+1 000 000 0000" />
                    </div>
                  </div>
                  <div className="mt-6">
                    <Label htmlFor="message">How can we help? *</Label>
                    <Textarea id="message" data-testid="contact-message" value={form.message} onChange={update("message")} className="mt-2 min-h-[140px]" placeholder="Tell us about your objectives, timeline and organization." />
                  </div>
                  <label data-testid="contact-auditguard" className={`mt-6 flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${auditguard ? "border-aero bg-aero/5" : "border-border hover:bg-secondary/60"}`}>
                    <Checkbox checked={auditguard} onCheckedChange={() => setAuditguard((v) => !v)} className="mt-0.5" />
                    <span className="text-sm font-medium text-navy">
                      I'm interested in <span className="font-semibold text-aero">AuditGuard AI</span> early access
                      <span className="mt-0.5 block text-xs font-normal text-muted-foreground">Be first to know when our flagship compliance platform launches.</span>
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-aero px-8 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {loading ? "Sending..." : "Send message"}
                  </button>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
